import { useMemo } from 'react';

import useGet from 'lib/hooks/useGet';
import { GLOBAL_PRODUCTS_BY_SEARCH } from 'lib/networking/endpoints';

/**
 * useUPBBetaProducts — fetches products for the Beta browse flow.
 *
 * Mirrors the existing UPB Viewer's call to GLOBAL_PRODUCTS_BY_SEARCH (params:
 * offset, limit, search_query, l1/l2/l3/l4_category_id, count_only,
 * with_l5_gmv_percentages, filter_products_with_active_dp_products). When a
 * search query is present, the category filters are dropped so the search
 * spans the whole catalog (matches the non-beta viewer's behavior).
 *
 * Returns { data: { data: products[], count }, loading, error, refetch }.
 */
function useUPBBetaProducts({
  offset = 0,
  limit = 50,
  searchQuery,
  l1CategoryId,
  l2CategoryId,
  l3CategoryId,
  l4CategoryId,
  skip = false,
} = {}) {
  const trimmed = (searchQuery || '').trim();
  const params = useMemo(() => {
    const p = {
      offset,
      limit,
      count_only: false,
      filter_products_with_active_dp_products: true,
      with_l5_gmv_percentages: Boolean(l4CategoryId),
      with_vendor_aggregates: true,
    };
    if (trimmed) {
      p.search_query = trimmed;
    } else {
      if (l1CategoryId) p.l1_category_id = l1CategoryId;
      if (l2CategoryId) p.l2_category_id = l2CategoryId;
      if (l3CategoryId) p.l3_category_id = l3CategoryId;
      if (l4CategoryId) p.l4_category_id = l4CategoryId;
    }
    return p;
  }, [offset, limit, trimmed, l1CategoryId, l2CategoryId, l3CategoryId, l4CategoryId]);

  return useGet(GLOBAL_PRODUCTS_BY_SEARCH, params, skip);
}

export default useUPBBetaProducts;
